import type {Site, SiteId} from '@/types/api';

interface SiteAccessCheckboxesProps {
    sites: Site[];
    selectedIds: SiteId[];
    onChange: (ids: SiteId[]) => void;
    disabled?: boolean;
}

export function SiteAccessCheckboxes({
    sites,
    selectedIds,
    onChange,
    disabled
}: SiteAccessCheckboxesProps) {
    const activeSites = sites.filter(s => s.status === 'active');

    const handleToggle = (siteId: SiteId) => {
        if (selectedIds.includes(siteId)) {
            onChange(selectedIds.filter(id => id !== siteId));
        } else {
            onChange([...selectedIds, siteId]);
        }
    };

    const handleSelectAll = () => {
        if (selectedIds.length === activeSites.length) {
            onChange([]);
        } else {
            onChange(activeSites.map(s => s.id));
        }
    };

    if (activeSites.length === 0) {
        return (
            <div
                style={{
                    padding: 20,
                    background: 'var(--tt-badge-bg)',
                    borderRadius: 8,
                    textAlign: 'center'
                }}
            >
                <p style={{fontSize: '1.3rem', color: 'var(--tt-text-secondary)', margin: 0}}>
                    No active newsletters available. Create a newsletter first.
                </p>
            </div>
        );
    }

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <label
                    style={{
                        fontSize: '1.3rem',
                        fontWeight: 600,
                        color: 'var(--tt-text-primary)'
                    }}
                >
                    Newsletter Access
                </label>
                <button
                    disabled={disabled}
                    style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.2rem',
                        color: 'var(--tt-accent)',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                        fontWeight: 500
                    }}
                    type="button"
                    onClick={handleSelectAll}
                >
                    {selectedIds.length === activeSites.length ? 'Deselect All' : 'Select All'}
                </button>
            </div>

            <div
                style={{
                    border: '1px solid var(--tt-card-border)',
                    borderRadius: 8,
                    maxHeight: 320,
                    overflowY: 'auto'
                }}
            >
                {activeSites.map((site, index) => (
                    <label
                        key={site.id}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '14px 16px',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            background: selectedIds.includes(site.id) ? 'rgba(239, 90, 43, 0.05)' : 'var(--tt-card-bg)',
                            borderBottom: index < activeSites.length - 1 ? '1px solid var(--tt-card-border)' : 'none',
                            transition: 'background 0.15s'
                        }}
                    >
                        <input
                            checked={selectedIds.includes(site.id)}
                            disabled={disabled}
                            style={{
                                width: 18,
                                height: 18,
                                accentColor: 'var(--tt-accent)',
                                cursor: disabled ? 'not-allowed' : 'pointer'
                            }}
                            type="checkbox"
                            onChange={() => handleToggle(site.id)}
                        />
                        <div style={{flex: 1}}>
                            <p
                                style={{
                                    fontSize: '1.4rem',
                                    fontWeight: 500,
                                    color: 'var(--tt-text-primary)',
                                    margin: 0
                                }}
                            >
                                {site.name}
                            </p>
                            <p
                                style={{
                                    fontSize: '1.2rem',
                                    color: 'var(--tt-text-secondary)',
                                    margin: '2px 0 0 0'
                                }}
                            >
                                {site.slug}.tribetale.com
                            </p>
                        </div>
                    </label>
                ))}
            </div>

        </div>
    );
}
